import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowUpRight, ArrowDownLeft, DollarSign, Mail, Phone } from "lucide-react";
import { format } from "date-fns";

interface ReferralListProps {
  userId: string;
  refreshKey?: number;
}

interface Referral {
  id: string;
  referred_by_id: string;
  referred_to_id: string;
  referral_message: string;
  contact_name: string;
  contact_email: string | null;
  contact_phone: string | null;
  status: string;
  closed_amount: number | null;
  chapter: string;
  created_at: string;
  referral_date: string;
  referred_by_name?: string;
  referred_by_company?: string;
  referred_to_name?: string;
  referred_to_company?: string;
}

const statusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  contacted: "bg-blue-100 text-blue-800 border-blue-200",
  closed_won: "bg-green-100 text-green-800 border-green-200",
  closed_lost: "bg-red-100 text-red-800 border-red-200",
};

const statusLabels: Record<string, string> = {
  pending: "Pending",
  contacted: "Contacted",
  closed_won: "Closed Won",
  closed_lost: "Closed Lost",
};

const ReferralList = ({ userId, refreshKey }: ReferralListProps) => {
  const [sent, setSent] = useState<Referral[]>([]);
  const [received, setReceived] = useState<Referral[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [myMemberId, setMyMemberId] = useState<string | null>(null);
  const [editingReferral, setEditingReferral] = useState<Referral | null>(null);
  const [editStatus, setEditStatus] = useState("");
  const [editAmount, setEditAmount] = useState("");
  const [savingUpdate, setSavingUpdate] = useState(false);

  const fetchReferrals = async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const { data: myRecord, error: meErr } = await supabase
        .from("members")
        .select("id")
        .eq("user_id", userId)
        .maybeSingle();
      if (meErr) throw meErr;

      if (!myRecord) {
        return;
      }

      setMyMemberId(myRecord.id);

      const { data: referrals, error: refErr } = await supabase
        .from("referrals")
        .select("*")
        .order("referral_date", { ascending: false })
        .order("created_at", { ascending: false });
      if (refErr) throw refErr;
      if (!referrals) return;

      const memberIds = [...new Set(referrals.flatMap(r => [r.referred_by_id, r.referred_to_id]))];
      const { data: memberNames, error: namesErr } = await supabase
        .from("members")
        .select("id, name, company")
        .in("id", memberIds);
      if (namesErr) throw namesErr;

      const memberMap = new Map(memberNames?.map(m => [m.id, m]) || []);

      const enriched = referrals.map(r => ({
        ...r,
        referred_by_name: memberMap.get(r.referred_by_id)?.name || "Unknown",
        referred_by_company: memberMap.get(r.referred_by_id)?.company || "",
        referred_to_name: memberMap.get(r.referred_to_id)?.name || "Unknown",
        referred_to_company: memberMap.get(r.referred_to_id)?.company || "",
      }));

      setSent(enriched.filter(r => r.referred_by_id === myRecord.id));
      setReceived(enriched.filter(r => r.referred_to_id === myRecord.id));
    } catch (err: any) {
      setLoadError(err?.message || "Couldn't load referrals.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferrals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, refreshKey]);

  const handleUpdateReferral = async () => {
    if (!editingReferral) return;

    const updates: any = { status: editStatus };
    if (editAmount) {
      updates.closed_amount = parseFloat(editAmount);
    }

    setSavingUpdate(true);
    try {
      const { data, error } = await supabase
        .from("referrals")
        .update(updates)
        .eq("id", editingReferral.id)
        .select();

      if (error) {
        toast.error("Couldn't save changes — check your connection and try again.");
        return;
      }
      if (!data || data.length === 0) {
        toast.error("You don't have permission to update this referral. Please contact an admin.");
        return;
      }

      toast.success("Referral updated");
      setEditingReferral(null);
      fetchReferrals();
    } catch {
      toast.error("Couldn't save changes — check your connection and try again.");
    } finally {
      setSavingUpdate(false);
    }
  };

  const openEdit = (referral: Referral) => {
    setEditingReferral(referral);
    setEditStatus(referral.status);
    setEditAmount(referral.closed_amount?.toString() || "");
  };

  if (loading) return <p className="text-center py-8 text-muted-foreground">Loading referrals...</p>;

  if (loadError) {
    return (
      <Card>
        <CardContent className="py-8 text-center space-y-3">
          <p className="text-sm text-muted-foreground">Couldn't load referrals.</p>
          <p className="text-xs text-muted-foreground">{loadError}</p>
          <Button variant="outline" size="sm" onClick={fetchReferrals}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const ReferralCard = ({ referral, type }: { referral: Referral; type: "sent" | "received" }) => (
    <Card className="mb-3">
      <CardContent className="py-4 px-5">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div className="flex-1 min-w-0 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              {type === "sent" ? (
                <span className="text-sm font-medium flex items-center gap-1">
                  <ArrowUpRight className="h-3.5 w-3.5 text-primary" />
                  To: {referral.referred_to_name} ({referral.referred_to_company})
                </span>
              ) : (
                <span className="text-sm font-medium flex items-center gap-1">
                  <ArrowDownLeft className="h-3.5 w-3.5 text-green-600" />
                  From: {referral.referred_by_name} ({referral.referred_by_company})
                </span>
              )}
              <Badge variant="outline" className={statusColors[referral.status] || ""}>
                {statusLabels[referral.status] || referral.status}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">{referral.referral_message}</p>
            <div className="flex items-center gap-4 text-xs text-muted-foreground pt-1">
              <span>Contact: {referral.contact_name}</span>
              {referral.contact_email && (
                <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{referral.contact_email}</span>
              )}
              {referral.contact_phone && (
                <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{referral.contact_phone}</span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {format(new Date(referral.referral_date + "T00:00:00"), "MMM d, yyyy")}
            </p>
            {type === "received" && referral.closed_amount != null && (
              <p className="text-xs flex items-center gap-1 text-green-600 font-medium">
                <DollarSign className="h-3 w-3" />
                {referral.closed_amount.toLocaleString()}
              </p>
            )}
          </div>
          {type === "received" && (
            <Button variant="outline" size="sm" onClick={() => openEdit(referral)}>
              Update
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <ArrowDownLeft className="h-5 w-5 text-green-600" />
          Referrals Received ({received.length})
        </h3>
        {received.length === 0 ? (
          <p className="text-sm text-muted-foreground">No referrals received yet.</p>
        ) : (
          received.map(r => <ReferralCard key={r.id} referral={r} type="received" />)
        )}
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <ArrowUpRight className="h-5 w-5 text-primary" />
          Referrals Sent ({sent.length})
        </h3>
        {sent.length === 0 ? (
          <p className="text-sm text-muted-foreground">No referrals sent yet. Use the form above to submit one!</p>
        ) : (
          sent.map(r => <ReferralCard key={r.id} referral={r} type="sent" />)
        )}
      </div>

      <Dialog open={!!editingReferral} onOpenChange={(open) => !open && setEditingReferral(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Referral</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={editStatus} onValueChange={setEditStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="closed_won">Closed Won</SelectItem>
                  <SelectItem value="closed_lost">Closed Lost</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Closed Amount (private — only you can see this)</Label>
              <Input
                type="number"
                placeholder="0.00"
                value={editAmount}
                onChange={(e) => setEditAmount(e.target.value)}
              />
            </div>
            <Button onClick={handleUpdateReferral} disabled={savingUpdate} className="w-full">
              {savingUpdate ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReferralList;
